// Shared API client for web and mobile apps

import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse, TransactionsResponse, User, Transaction } from '../types';

export class ApiClient {
  private client: AxiosInstance;

  constructor(baseURL: string = 'https://budgetapp.se/api') {
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = this.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.clearToken();
          // Don't auto-redirect, let the AuthContext handle it
          console.log('401 Unauthorized - token cleared');
        }
        return Promise.reject(error);
      }
    );
  }

  // Token management (platform-specific)
  private getToken(): string | null {
    if (typeof window !== 'undefined') {
      // Web: localStorage
      return localStorage.getItem('token');
    } else {
      // Mobile: AsyncStorage (imported by mobile app)
      return null; // Will be handled by mobile app
    }
  }

  private clearToken(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  }

  // Auth methods
  async loginWithApple(identityToken: string, fullName?: string): Promise<ApiResponse<{ user: User; token: string }>> {
    const response: AxiosResponse<ApiResponse<{ user: User; token: string }>> = await this.client.post('/auth/apple', {
      identityToken,
      fullName,
    });
    return response.data;
  }

  async verifyToken(): Promise<ApiResponse<User>> {
    const response: AxiosResponse<ApiResponse<User>> = await this.client.get('/auth/verify');
    return response.data;
  }

  // Transaction methods
  async getTransactions(): Promise<TransactionsResponse> {
    const response: AxiosResponse<TransactionsResponse> = await this.client.get('/transactions');
    return response.data;
  }

  async createTransaction(transaction: Partial<Transaction>): Promise<ApiResponse<Transaction>> {
    const response: AxiosResponse<ApiResponse<Transaction>> = await this.client.post('/transactions', transaction);
    return response.data;
  }

  async updateTransaction(id: number, transaction: Partial<Transaction>): Promise<ApiResponse<Transaction>> {
    const response: AxiosResponse<ApiResponse<Transaction>> = await this.client.put(`/transactions/${id}`, transaction);
    return response.data;
  }

  async deleteTransaction(id: number): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.client.delete(`/transactions/${id}`);
    return response.data;
  }

  // Generic HTTP methods for flexibility
  async get<T = any>(url: string): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.get(url);
    return response.data;
  }

  // For endpoints that return a file rather than JSON. A plain <a href> to
  // one of those never carries the Authorization header, so the API answers
  // "Access token required" instead of the document.
  async getBlob(url: string): Promise<Blob> {
    const response = await this.client.get(url, {
      responseType: 'blob',
      timeout: 60000,
    });
    return response.data as Blob;
  }

  // Multipart uploads: the Content-Type must carry the boundary the browser
  // generates, so the JSON default has to be cleared rather than overridden.
  // Same idea as getBlob, but the server needs the list of ids to package.
  async postBlob(url: string, data: any): Promise<Blob> {
    const response = await this.client.post(url, data, {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data as Blob;
  }

  async postForm<T = any>(url: string, form: FormData): Promise<ApiResponse<T>> {
    const response = await this.client.post(url, form, {
      headers: { 'Content-Type': undefined as any },
      timeout: 120000,
    });
    return response.data;
  }

  async post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.post(url, data);
    return response.data;
  }

  async put<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.put(url, data);
    return response.data;
  }

  async delete<T = any>(url: string): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.delete(url);
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
